import React, { useState, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Stack,
  TextField,
  useTheme,
  IconButton,
  Collapse,
  Chip,
  Select,
  MenuItem,
} from '@mui/material';
import Swal from 'sweetalert2';
import { bulkGrantPremium, validateBulkPremiumUsers } from '../../../services/userServices';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { alpha } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import PremiumGrantHistoryModal from './PremiumGrantHistoryModal';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

const PremiumManagementPage = () => {
  const [rows, setRows] = useState([{
    id: crypto.randomUUID(),
    email: '',
    name: '',
    duration: '1yr',
    reason: '',
    braze_tag: '',
    status: 'pending',
    error: null
  }]);
  const setRowsRef = useRef();
  setRowsRef.current = setRows;
  const [processing, setProcessing] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(true);
  const durationOptions = ['1wk', '2wk', '1mo', '3mo', '6mo', '1yr', 'lifetime'];
  const [originalRows, setOriginalRows] = useState([]);
  const [inputList, setInputList] = useState('');
  const fileInputRef = useRef();
  const globalReason = '';

  const PROCESS_BATCH_SIZE = 25;
  const VALIDATE_BATCH_SIZE = 50;

  const handleProcess = async () => {
    // Filter out rows that are already processed successfully
    const rowsToProcess = rows.filter(row => !['success', 'created'].includes(row.status));

    if (rowsToProcess.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nothing to Process',
        text: 'All rows have been processed successfully.'
      });
      return;
    }

    // Validate required fields
    const invalidRows = rowsToProcess.map((row, index) => ({
      index,
      missingFields: [
        !row.email?.trim() ? 'Email' : null,
        // Only validate name if account doesn't exist yet
        !['existing', 'existing_premium'].includes(row.status) && !row.name?.trim() ? 'Name' : null,
        !row.duration?.trim() ? 'Duration' : null,
        !row.reason?.trim() ? 'Reason' : null,
      ].filter(Boolean)
    })).filter(row => row.missingFields.length > 0);

    if (invalidRows.length > 0) {
      // Update rows to show validation errors
      setRows(prevRows => prevRows.map((row) => {
        // Find if this row is in rowsToProcess and has validation errors
        const rowIndex = rowsToProcess.findIndex(r => r.email === row.email);
        const invalidRow = rowIndex !== -1 ? invalidRows.find(r => r.index === rowIndex) : null;
        if (invalidRow) {
          return {
            ...row,
            status: 'error',
            error: `Required fields missing: ${invalidRow.missingFields.join(', ')}`,
            showError: true
          };
        }
        return row;
      }));

      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: `Please fill in all required fields. ${invalidRows.length} row(s) have missing information.`
      });
      return;
    }

    setProcessing(true);
    const usersToSend = rowsToProcess.map(({ email, name, duration, reason, braze_tag }) => ({ email, name, duration, reason, braze_tag }));
    const totalBatches = Math.ceil(usersToSend.length / PROCESS_BATCH_SIZE);

    // Maintain an in-function status map keyed by lowercased email — this is
    // the source of truth for the summary. Seed only from `rowsToProcess` so
    // the count reflects what *this* invocation actually attempted, not the
    // cumulative table state. (Seeding from `rows` inflated the tally on
    // re-runs: e.g. retrying 5 errored rows out of 100 would have reported
    // "100 granted".) Keys are lowercased so the API normalizing email case
    // in its response can't insert a second entry under a different-case key
    // and double-count the same user — matches the dedup step's convention.
    // The local map avoids two other hazards as well: (a) calling Swal inside
    // a setRows updater (React requires updaters to be pure; StrictMode would
    // double-fire the modal); (b) racing the React batch when reading "final"
    // state right after the loop.
    const statusByEmail = new Map(rowsToProcess.map(r => [r.email.toLowerCase(), { status: r.status, error: r.error }]));

    for (let i = 0; i < totalBatches; i++) {
      const batch = usersToSend.slice(i * PROCESS_BATCH_SIZE, (i + 1) * PROCESS_BATCH_SIZE);
      setProgressText(`Processing batch ${i + 1}/${totalBatches}…`);
      try {
        const resp = await bulkGrantPremium(batch);
        const resultsArray = Array.isArray(resp?.objects) ? resp.objects : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
        for (const r of resultsArray) {
          const key = (r.email || '').toLowerCase();
          const existing = statusByEmail.get(key);
          if (existing && ['success', 'created'].includes(existing.status)) continue;
          statusByEmail.set(key, { status: r.status, error: r.error ?? null });
        }
        setRows(prevRows => prevRows.map(row => {
          const match = resultsArray.find(item => (item.email || '').toLowerCase() === row.email.toLowerCase());
          if (!match || ['success', 'created'].includes(row.status)) return row;
          return { ...row, status: match.status, error: match.error ?? null };
        }));
      } catch (err) {
        // Mark this batch's rows as errors but continue with remaining batches
        const batchEmails = new Set(batch.map(u => u.email.toLowerCase()));
        for (const email of batchEmails) {
          const existing = statusByEmail.get(email);
          if (existing && ['success', 'created'].includes(existing.status)) continue;
          statusByEmail.set(email, { status: 'error', error: 'Request failed — please retry this batch' });
        }
        setRows(prevRows => prevRows.map(row => {
          if (!batchEmails.has(row.email.toLowerCase()) || ['success', 'created'].includes(row.status)) return row;
          return { ...row, status: 'error', error: 'Request failed — please retry this batch' };
        }));
      }
    }

    setProcessing(false);
    setProgressText('');

    let granted = 0, created = 0, errors = 0, validationFailed = 0;
    for (const { status } of statusByEmail.values()) {
      if (status === 'success') granted++;
      else if (status === 'created') created++;
      else if (status === 'error') errors++;
      else if (status === 'validation_failed') validationFailed++;
    }

    const parts = [];
    if (granted > 0) parts.push(`${granted} granted`);
    if (created > 0) parts.push(`${created} created`);
    if (errors > 0) parts.push(`${errors} errors`);
    if (validationFailed > 0) parts.push(`${validationFailed} still need validation`);
    const hasIssues = errors > 0 || validationFailed > 0;

    Swal.fire({
      icon: hasIssues ? 'warning' : 'success',
      title: 'Processing Complete',
      text: parts.join(', ') || 'No users processed.',
      footer: hasIssues
        ? 'Errored or unvalidated rows stayed in the table — fix and click Process again to retry only those.'
        : undefined,
    });
  };

  // Add new empty row
  const handleAddRow = () => {
    setRows(prev => ([
      ...prev,
      {
        id: crypto.randomUUID(),
        email: '',
        name: '',
        duration: durationOptions[0],
        reason: '',
        braze_tag: '',
        status: 'pending',
        error: null
      }
    ]));
  };

  const handleRemoveRow = (index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index, field) => (e) => {
    const value = e.target.value;
    setRows(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  // Reset loaded CSV data to original
  const handleResetCsv = () => {
    setRows(originalRows);
  };

  // Clear all rows
  const handleClearAll = () => {
    setRows([]);
  };

  // Clear only successful rows
  const handleClearSuccessful = () => {
    setRows(prevRows => prevRows.filter(row => !['success', 'created'].includes(row.status)));
  };

  // Parse pasted or uploaded list, then validate new rows
  const parseUserList = async (text) => {
    const lines = text.trim().split(/\r?\n/);
    let newData = [];
    for (const line of lines) {
      if (!line.trim()) continue;
      let email = '', name = '', duration = '', reason = '', braze_tag = '';
      let parts = line.split(',').map(v => v.trim());
      if (parts.length === 1) {
        email = parts[0];
        if (!email || email.toLowerCase() === 'user_email') continue;
        name = email.split('@')[0].replace(/\./g, ' ');
        duration = '1yr';
        reason = globalReason;
        braze_tag = '';
      } else if (parts.length === 2) {
        email = parts[0];
        name = parts[1] || email.split('@')[0].replace(/\./g, ' ');
        duration = '1yr';
        reason = globalReason;
        braze_tag = '';
        if (!email || email.toLowerCase() === 'user_email') continue;
      } else if (parts.length === 3) {
        email = parts[0];
        name = parts[1] || email.split('@')[0].replace(/\./g, ' ');
        duration = parts[2] || '1yr';
        reason = globalReason;
        braze_tag = '';
        if (!email || email.toLowerCase() === 'user_email') continue;
      } else if (parts.length === 4) {
        email = parts[0];
        name = parts[1] || email.split('@')[0].replace(/\./g, ' ');
        duration = parts[2] || '1yr';
        reason = parts[3] || globalReason;
        braze_tag = '';
        if (!email || email.toLowerCase() === 'user_email') continue;
      } else {
        email = parts[0];
        name = parts[1] || email.split('@')[0].replace(/\./g, ' ');
        duration = parts[2] || '1yr';
        reason = parts[3] || globalReason;
        braze_tag = parts[4] || '';
        if (!email || email.toLowerCase() === 'user_email') continue;
      }
      newData.push({
        id: crypto.randomUUID(),
        email,
        name,
        duration,
        reason,
        braze_tag,
        status: 'pending',
        error: null
      });
    }
    // Dedupe by lowercased email BEFORE validation. CSV exports from sheets
    // commonly contain duplicate addresses, and the previous flow let dupes
    // through — they'd ride through validation/processing and inflate counts
    // (and the second occurrence overwriting the first produced phantom
    // "errors" in the summary swal). Surface the dedup count so the operator
    // can sanity-check against their source list.
    const seenEmails = new Set();
    const dedupedNewData = [];
    let duplicateCount = 0;
    for (const row of newData) {
      const key = row.email.toLowerCase();
      if (seenEmails.has(key)) {
        duplicateCount++;
        continue;
      }
      seenEmails.add(key);
      dedupedNewData.push(row);
    }
    newData = dedupedNewData;

    if (duplicateCount > 0) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: `${duplicateCount} duplicate ${duplicateCount === 1 ? 'email' : 'emails'} merged`,
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });
    }

    if (newData.length > 0) {
      // Validate in batches to avoid timeouts on large lists. If a batch
      // fails (network, ALB hiccup, etc.) mark its rows as "validation_failed"
      // with the error message so the operator can retry — silently leaving
      // them as "pending" was the root cause of operators not knowing rows
      // were actually unprocessed (Monday 11980621297).
      const usersToValidate = newData.map(({ email, name, duration, reason }) => ({ email, name, duration, reason }));
      for (let i = 0; i < usersToValidate.length; i += VALIDATE_BATCH_SIZE) {
        const batch = usersToValidate.slice(i, i + VALIDATE_BATCH_SIZE);
        try {
          const resp = await validateBulkPremiumUsers(batch);
          const resultsArray = Array.isArray(resp?.objects) ? resp.objects : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
          for (const result of resultsArray) {
            // Case-insensitive match — same convention as the dedup step and
            // handleProcess. The API may normalize email casing in its response.
            const match = newData.find(row => row.email.toLowerCase() === (result.email || '').toLowerCase());
            if (match) {
              match.status = result.status;
              match.error = result.error ?? null;
            }
          }
        } catch (err) {
          const batchEmails = new Set(batch.map(u => u.email.toLowerCase()));
          for (const row of newData) {
            if (batchEmails.has(row.email.toLowerCase())) {
              row.status = 'validation_failed';
              row.error = err?.message || 'Could not reach the server. Click "Retry Validation" to try again.';
            }
          }
        }
      }
    }
    // Remove any empty rows before setting the new data
    const isEmptyRow = (row) => !row.email && !row.name && !row.reason && !row.braze_tag && row.duration === '1yr' && row.status === 'pending';
    // Merge with existing rows: update if email exists, else add
    setRows(prev => {
      const filteredPrev = prev.filter(row => !isEmptyRow(row));
      const emailMap = new Map(filteredPrev.map(r => [r.email.toLowerCase(), r]));
      newData.forEach(newRow => {
        const key = newRow.email.toLowerCase();
        if (emailMap.has(key)) {
          // Update existing row fields
          const existing = emailMap.get(key);
          Object.assign(existing, newRow);
        } else {
          emailMap.set(key, newRow);
        }
      });
      return Array.from(emailMap.values());
    });
    setOriginalRows(prev => {
      const filteredPrev = prev.filter(row => !isEmptyRow(row));
      const emailMap = new Map(filteredPrev.map(r => [r.email.toLowerCase(), r]));
      newData.forEach(newRow => {
        const key = newRow.email.toLowerCase();
        if (emailMap.has(key)) {
          const existing = emailMap.get(key);
          Object.assign(existing, newRow);
        } else {
          emailMap.set(key, newRow);
        }
      });
      return Array.from(emailMap.values());
    });
  };

  // Handle file upload for CSV
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => parseUserList(evt.target.result);
      reader.readAsText(file);
      // Reset file input so same file can be re-uploaded if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle load from textarea
  const handleLoadInputList = () => {
    if (inputList.trim()) {
      parseUserList(inputList);
      setInputList('');
    }
  };

  // Validate rows with backend
  const handleValidate = async () => {
    if (rows.length === 0) return;
    setProcessing(true);
    try {
      const usersToValidate = rows.map(({ email, name, duration, reason }) => ({ email, name, duration, reason }));
      const resp = await validateBulkPremiumUsers(usersToValidate);
      const resultsArray = Array.isArray(resp?.objects) ? resp.objects : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
      setRows(prevRows => prevRows.map(row => {
        // Case-insensitive match — see handleProcess / handleRetryValidation.
        const match = resultsArray.find(item => (item.email || '').toLowerCase() === row.email.toLowerCase());
        if (!match) return row;
        return {
          ...row,
          status: match.status,
          error: match.error ?? null,
        };
      }));
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: err?.message || 'Validation failed.' });
    } finally {
      setProcessing(false);
    }
  };

  // Validate a single row (by index) after email edit
  const validateRow = async (rowIdx) => {
    const row = rows[rowIdx];
    if (!row) return;
    try {
      const resp = await validateBulkPremiumUsers([{ email: row.email, name: row.name, duration: row.duration, reason: row.reason }]);
      const resultsArray = Array.isArray(resp?.objects) ? resp.objects : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
      const result = resultsArray[0] || null;
      if (result) {
        setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, status: result.status, error: result.error ?? null } : r));
      }
    } catch (err) {
      // If validation fails, keep as is
    }
  };

  // Re-run validation for any rows currently in 'validation_failed' or
  // 'pending' state. Triggered by the "Retry Validation" button when the
  // operator wants to recover from a transient network failure during the
  // initial CSV import.
  const handleRetryValidation = async () => {
    const failedRows = rows.filter(r => ['validation_failed', 'pending'].includes(r.status) && r.email?.trim());
    if (failedRows.length === 0) return;

    setProcessing(true);
    setProgressText('Retrying validation…');
    try {
      const usersToValidate = failedRows.map(r => ({ email: r.email, name: r.name, duration: r.duration, reason: r.reason }));
      let networkFailedCount = 0;   // batch couldn't reach the server
      let validationErrorCount = 0; // server returned status: 'error' for the row
      let validatedCount = 0;       // server returned a valid status (existing/needs_account/etc.)
      for (let i = 0; i < usersToValidate.length; i += VALIDATE_BATCH_SIZE) {
        const batch = usersToValidate.slice(i, i + VALIDATE_BATCH_SIZE);
        try {
          const resp = await validateBulkPremiumUsers(batch);
          const resultsArray = Array.isArray(resp?.objects) ? resp.objects : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
          for (const r of resultsArray) {
            if (r.status === 'error') validationErrorCount++;
            else validatedCount++;
          }
          setRows(prev => prev.map(row => {
            // Case-insensitive match — same convention as handleProcess and the
            // dedup step. If the API normalizes email case in its response,
            // strict equality would miss every row and leave them stuck in
            // validation_failed despite a successful response.
            const match = resultsArray.find(item => (item.email || '').toLowerCase() === row.email.toLowerCase());
            // Never overwrite a row that's already been granted/created.
            // Retry only operates on validation_failed/pending rows but the
            // setRows match is by email, so a successful row sharing an email
            // (manual dup, two paste passes, etc.) could otherwise get its
            // status clobbered back to a validation outcome.
            if (!match || ['success', 'created'].includes(row.status)) return row;
            return { ...row, status: match.status, error: match.error ?? null };
          }));
        } catch (err) {
          const batchEmails = new Set(batch.map(u => u.email.toLowerCase()));
          networkFailedCount += batch.length;
          setRows(prev => prev.map(row => {
            if (!batchEmails.has(row.email.toLowerCase()) || ['success', 'created'].includes(row.status)) return row;
            return {
              ...row,
              status: 'validation_failed',
              error: err?.message || 'Validation request failed — try again or check your connection.',
            };
          }));
        }
      }

      // Distinguish unreachable batches (retry-worthy) from per-row server
      // rejections (operator must edit the row). Bugbot caught the original
      // version always reporting "success" when no batch threw, even if the
      // API marked individual rows as `error`.
      const hasIssues = networkFailedCount > 0 || validationErrorCount > 0;
      const parts = [];
      if (validatedCount > 0) parts.push(`${validatedCount} validated`);
      if (validationErrorCount > 0) parts.push(`${validationErrorCount} rejected by server`);
      if (networkFailedCount > 0) parts.push(`${networkFailedCount} couldn't reach the server`);

      Swal.fire({
        icon: hasIssues ? 'warning' : 'success',
        title: hasIssues ? 'Validation Retry Finished With Issues' : 'Validation Retry Complete',
        text: parts.join(', ') || 'No rows retried.',
        footer: networkFailedCount > 0
          ? 'Rows that couldn\'t reach the server stayed as Validation Failed — click Retry Validation again to try once more.'
          : (validationErrorCount > 0 ? 'Rows marked Error were rejected by the server — open the row to see why.' : undefined),
      });
    } finally {
      setProcessing(false);
      setProgressText('');
    }
  };

  return (
    <>
      <MainContentContainer>
        {/* Top App Bar */}
        <AppBar position="sticky" sx={{ mb: 3 }}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Premium Access Requests
            </Typography>
            <Button
              color="inherit"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryModalOpen(true)}
            >
              View History
            </Button>
          </Toolbar>
        </AppBar>

        {/* Table section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography color="text.primary">
                Premium Access Table ({rows.length} rows)
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button startIcon={<AddIcon />} onClick={handleAddRow}>Add Row</Button>
              <Button
                startIcon={<ClearAllIcon />}
                onClick={handleClearAll}
                color="warning"
                disabled={rows.length === 0}
              >
                Clear All
              </Button>
              <Button
                startIcon={<ClearAllIcon />}
                onClick={handleClearSuccessful}
                color="info"
                disabled={!rows.some(row => ['success', 'created'].includes(row.status))}
              >
                Clear Successful
              </Button>
              {rows.some(row => row.status === 'validation_failed') && (
                // Only surfaces when at least one row failed validation. Lets
                // the operator retry without re-uploading the whole CSV.
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleRetryValidation}
                  disabled={processing}
                >
                  Retry Validation
                </Button>
              )}
              <Button
                startIcon={<SendIcon />}
                variant="contained"
                color="primary"
                onClick={handleProcess}
                disabled={processing || rows.length === 0}
              >
                {processing ? (progressText || 'Processing…') : 'Process'}
              </Button>
            </Stack>
          </Stack>
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No rows. Add a row manually or upload a CSV file in the section below.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Braze Tag</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => {
                  const hasError = ['error', 'validation_failed'].includes(row.status) && row.error;
                  const chipProps = (() => {
                    switch (row.status) {
                      case 'success': return { label: 'Success', color: 'success' };
                      case 'existing': return { label: 'Pending Grant Only', color: 'success' };
                      case 'existing_premium': return { label: 'Has Premium — Will Extend', color: 'info' };
                      case 'needs_account': return { label: 'Pending Create Account', color: 'warning' };
                      case 'created': return { label: 'Created', color: 'success' };
                      case 'error': return { label: 'Error', color: 'error' };
                      // validation_failed = network/server failure during the
                      // pre-process validation step. Distinct from 'pending'
                      // (never validated) so the operator knows to retry.
                      case 'validation_failed': return { label: 'Validation Failed', color: 'warning' };
                      default: return { label: 'Pending', color: 'default', variant: 'outlined' };
                    }
                  })();
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        sx={(theme) => ({
                          backgroundColor:
                            row.status === 'success'
                              ? alpha(theme.palette.success.main, 0.15)
                              : row.status === 'error'
                                ? alpha(theme.palette.error.main, 0.15)
                                : idx % 2 === 0
                                  ? 'transparent'
                                  : theme.palette.action.hover,
                          '&:hover': { backgroundColor: theme.palette.action.selected },
                        })}
                      >
                        <TableCell width="25%">
                          <TextField
                            variant="standard"
                            autoComplete='off'
                            name="email"
                            value={row.email}
                            onChange={handleFieldChange(idx, 'email')}
                            onBlur={() => validateRow(idx)}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell width="20%">
                          <TextField variant="standard" autoComplete='off' name="name" value={row.name} onChange={handleFieldChange(idx, 'name')} fullWidth />
                        </TableCell>
                        <TableCell>
                          <Select
                            variant="standard"
                            name="duration"
                            value={row.duration}
                            onChange={handleFieldChange(idx, 'duration')}
                            sx={{ minWidth: durationOptions.reduce((max, opt) => Math.max(max, opt.length), 0) + 3 + 'ch' }}
                          >
                            {durationOptions.map(opt => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
                          </Select>
                        </TableCell>
                        <TableCell width="25%">
                          <TextField variant="standard" autoComplete='off' name="reason" value={row.reason || ''} onChange={handleFieldChange(idx, 'reason')} fullWidth />
                        </TableCell>
                        <TableCell width="15%">
                          <TextField variant="standard" autoComplete='off' name="braze_tag" value={row.braze_tag || ''} onChange={handleFieldChange(idx, 'braze_tag')} fullWidth />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {hasError && (
                              <IconButton size="small" color="error" onClick={() => setRows(prev => {
                                const copy = [...prev];
                                copy[idx] = { ...copy[idx], showError: !copy[idx].showError };
                                return copy;
                              })}>
                                <ExpandMoreIcon sx={{ transform: row.showError ? 'rotate(180deg)' : 'none' }} />
                              </IconButton>
                            )}
                            <Chip size="small" {...chipProps} />
                          </Stack>
                        </TableCell>
                        <TableCell align="center" width="60">
                          <IconButton size="small" color="error" onClick={() => handleRemoveRow(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {hasError && row.showError && (
                        <TableRow key={row.id + '-error'}>
                          <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                            <Collapse in={true} timeout="auto" unmountOnExit>
                              <Box sx={{ m: 1 }}>
                                <Typography variant="body2" color="error.main">{row.error}</Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>

        {/* Instructions */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Button
            onClick={() => setInstructionsOpen(!instructionsOpen)}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{
                  transform: instructionsOpen ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{ mb: instructionsOpen ? 2 : 0 }}
          >
            <Typography variant="h6" color="text.primary">
              Instructions
            </Typography>
          </Button>
          <Collapse in={instructionsOpen}>
            <Box>
              <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                <b>Step 1:</b> Add individual users to the table above <b><u>OR</u></b> use the section below to load multiple users at once.
              </Typography>
              <Box color="text.secondary" sx={{ mb: 2 }}>
                <ul>
                  <li><b>Email:</b> A valid email address is required.</li>
                  <li><b>Name:</b> Enter First Name OR First and Last Name.</li>
                  <li><b>Duration:</b> Is required and access will automatically expire after the period granted.</li>
                  <li><b>Reason:</b> Short description for why free access is being given.</li>
                  <li><b>Braze Tag (Optional):</b> Will be set as a Braze user property if provided.</li>
                </ul>
              </Box>

              <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                <b>Step 2:</b> Once all users have been added, click the <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<SendIcon />}
                  sx={{ pointerEvents: 'none', mx: 1 }}
                >
                  Process
                </Button> button in the table above.
              </Typography>
              <Box color="text.secondary">
                <ul>
                  <li>If successful, a green <Chip size="small" label="Success" color="success" sx={{ mx: 1 }} /> tag will appear next to each row.</li>
                  <li>If a red <Chip size="small" label="Error" color="error" sx={{ mx: 1 }} /> tag appears, edit the rows with errors and click <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<SendIcon />}
                    sx={{ pointerEvents: 'none', mx: 1 }}
                  >
                    Process
                  </Button> again until all are added successfully.</li>
                  <li>If an amber <Chip size="small" label="Validation Failed" color="warning" sx={{ mx: 1 }} /> tag appears, the row couldn't reach the server during the initial check (network blip). Click the <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    sx={{ pointerEvents: 'none', mx: 1 }}
                  >
                    Retry Validation
                  </Button> button (only shown when at least one row needs it) to re-run validation for those rows.</li>
                  <li>Duplicate emails in your CSV/paste are automatically merged on import — a notice will pop up showing how many were collapsed.</li>
                  <li>If an app account exists for an email provided, the user will be granted the specified period of premium access.</li>
                  <li>If an app account doesn't exist for an email provided, the account will be created and an email will be sent to the user on how to set their password to access their account, and the account will have premium access for the period specified.</li>
                </ul>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {/* CSV Upload Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Button
            onClick={() => setBulkAddOpen(!bulkAddOpen)}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{
                  transform: bulkAddOpen ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" color="text.primary">
              Load Multiple Users at Once
            </Typography>
          </Button>
          <Collapse in={bulkAddOpen}>
            <Box color="text.secondary" sx={{ mb: 2 }}>
              You can either:
              <ul>
                <li>Type or paste your list of users in the text box below and click the <Button variant="contained" size="small" sx={{ pointerEvents: 'none', mx: 1 }}>Load</Button> button to add them to the table above, or</li>
                <li>Upload a CSV file using the "Upload CSV" button (following the format examples shown below)</li>
              </ul>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
              <TextField
                label="Paste user data (one per line)"
                value={inputList}
                onChange={e => setInputList(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                placeholder={`john@doe.com\njane@smith.com,Jane Smith\nbob@bob.com,Bob,1yr,Special promo\nalice@alice.com,Alice Jones,6mo,Marketing campaign,tag1`}
              />
              <Stack direction="column" spacing={1} alignItems="flex-start">
                <Button variant="contained" onClick={handleLoadInputList} disabled={!inputList.trim()} sx={{ minWidth: 120 }}>Load</Button>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{ minWidth: 120, whiteSpace: 'nowrap' }}
                >
                  Upload CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                </Button>
              </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '12px' }}>
              Available columns: Email, Name, Duration, Reason, Braze Tag<br />
              <br />
              Format Examples (comma separated):<br />
              Basic: Just email<br />
              <code>john@doe.com</code><br />
              With name:<br />
              <code>jane@smith.com,Jane Smith</code><br />
              With name and duration:<br />
              <code>bob@bob.com,Bob,6mo</code><br />
              Complete format (Email,Name,Duration,Reason,Braze Tag):<br />
              <code>alice@alice.com,Alice,1yr,Special promo reason,tag1</code><br />
              Available duration options: 1wk, 2wk, 1mo, 3mo, 6mo, 1yr, lifetime (defaults to 1yr if not specified)
            </Typography>
          </Collapse>
        </Paper>
      </MainContentContainer>

      {/* History Modal */}
      < PremiumGrantHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
      />
    </>
  );
};

export default PremiumManagementPage; 